/**
 * Invoice Export Utility
 * ======================
 * Generates Excel files with invoices grouped by month into separate sheets.
 */

import * as XLSX from 'xlsx';
import { DocumentItem } from '../services/documentApi';

interface ExportOptions {
    startDate: Date;
    endDate: Date;
    filename?: string;
}

interface InvoiceExportRow {
    'Dosya Adi': string;
    'Tedarikci': string;
    'Fatura No': string;
    'Fatura Tarihi': string;
    'Toplam Tutar': string;
    'Para Birimi': string;
    'KDV': string;
    'Alt Toplam': string;
    'Kalem Sayisi': number;
    'Extraction Durumu': string;
    'Yuklenme Tarihi': string;
}

const TURKISH_MONTHS = [
    'Ocak', 'Subat', 'Mart', 'Nisan', 'Mayis', 'Haziran',
    'Temmuz', 'Agustos', 'Eylul', 'Ekim', 'Kasim', 'Aralik'
];

const getMonthYearKey = (date: Date): string => {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
};

const getMonthYearLabel = (date: Date): string => {
    return `${TURKISH_MONTHS[date.getMonth()]} ${date.getFullYear()}`;
};

const formatCurrency = (amount?: number): string => {
    if (amount === undefined || amount === null) return '-';
    return amount.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const formatDate = (dateStr?: string): string => {
    if (!dateStr) return '-';
    try {
        return new Date(dateStr).toLocaleDateString('tr-TR');
    } catch {
        return dateStr;
    }
};

const getExtractionStatusLabel = (status?: string): string => {
    const statusMap: Record<string, string> = {
        'completed': 'Tamamlandi',
        'processing': 'Isleniyor',
        'pending': 'Bekliyor',
        'failed': 'Basarisiz'
    };
    return statusMap[status || ''] || status || 'Bilinmiyor';
};

const transformInvoiceToRow = (doc: DocumentItem): InvoiceExportRow => {
    const extracted = doc.extracted_data;
    const financials = extracted?.financials;

    return {
        'Dosya Adi': doc.filename || '-',
        'Tedarikci': extracted?.vendor?.name || extracted?.supplier_name || '-',
        'Fatura No': extracted?.invoice_number || '-',
        'Fatura Tarihi': formatDate(extracted?.invoice_date),
        'Toplam Tutar': formatCurrency(financials?.total_amount || extracted?.total_amount),
        'Para Birimi': financials?.currency || extracted?.currency || 'TRY',
        'KDV': formatCurrency(financials?.tax),
        'Alt Toplam': formatCurrency(financials?.subtotal),
        'Kalem Sayisi': extracted?.line_items?.length || 0,
        'Extraction Durumu': getExtractionStatusLabel(doc.extraction_status),
        'Yuklenme Tarihi': formatDate(doc.created_at)
    };
};

const groupInvoicesByMonth = (
    invoices: DocumentItem[],
    startDate: Date,
    endDate: Date
): Map<string, DocumentItem[]> => {
    const grouped = new Map<string, DocumentItem[]>();

    // Filter invoices by date range
    const filtered = invoices.filter(inv => {
        const invDate = new Date(inv.created_at);
        return invDate >= startDate && invDate <= endDate;
    });

    // Sort by date descending
    filtered.sort((a, b) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    );

    // Group by month
    filtered.forEach(inv => {
        const key = getMonthYearKey(new Date(inv.created_at));
        if (!grouped.has(key)) {
            grouped.set(key, []);
        }
        grouped.get(key)!.push(inv);
    });

    return grouped;
};

export const exportInvoicesToExcel = (
    invoices: DocumentItem[],
    options: ExportOptions
): void => {
    const { startDate, endDate, filename } = options;

    // Group invoices by month
    const groupedByMonth = groupInvoicesByMonth(invoices, startDate, endDate);

    if (groupedByMonth.size === 0) {
        alert('Secilen tarih araliginda fatura bulunamadi.');
        return;
    }

    // Create workbook
    const workbook = XLSX.utils.book_new();

    // Sort months (newest first)
    const sortedMonths = Array.from(groupedByMonth.keys()).sort().reverse();

    // Create a sheet for each month
    sortedMonths.forEach(monthKey => {
        const monthInvoices = groupedByMonth.get(monthKey)!;
        const rows = monthInvoices.map(transformInvoiceToRow);

        // Create worksheet
        const worksheet = XLSX.utils.json_to_sheet(rows);

        // Set column widths
        worksheet['!cols'] = [
            { wch: 30 }, // Dosya Adi
            { wch: 25 }, // Tedarikci
            { wch: 15 }, // Fatura No
            { wch: 12 }, // Fatura Tarihi
            { wch: 15 }, // Toplam Tutar
            { wch: 10 }, // Para Birimi
            { wch: 12 }, // KDV
            { wch: 12 }, // Alt Toplam
            { wch: 12 }, // Kalem Sayisi
            { wch: 15 }, // Extraction Durumu
            { wch: 12 }  // Yuklenme Tarihi
        ];

        // Get sheet name from month/year
        const sampleDate = new Date(monthKey + '-01');
        const sheetName = getMonthYearLabel(sampleDate);

        // Add to workbook
        XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
    });

    // Add summary sheet
    const summaryData = sortedMonths.map(monthKey => {
        const monthInvoices = groupedByMonth.get(monthKey)!;
        const sampleDate = new Date(monthKey + '-01');

        // Calculate totals per currency
        const totals: Record<string, number> = {};
        monthInvoices.forEach(inv => {
            const amount = inv.extracted_data?.financials?.total_amount ||
                           inv.extracted_data?.total_amount || 0;
            const currency = inv.extracted_data?.financials?.currency || inv.extracted_data?.currency || 'TRY';
            totals[currency] = (totals[currency] || 0) + amount;
        });

        const totalStr = Object.entries(totals)
            .map(([curr, amt]) => `${formatCurrency(amt)} ${curr}`)
            .join(', ') || '-';

        return {
            'Ay': getMonthYearLabel(sampleDate),
            'Fatura Sayisi': monthInvoices.length,
            'Toplam Tutar': totalStr
        };
    });

    const summarySheet = XLSX.utils.json_to_sheet(summaryData);
    summarySheet['!cols'] = [
        { wch: 20 },
        { wch: 15 },
        { wch: 30 }
    ];
    XLSX.utils.book_append_sheet(workbook, summarySheet, 'Ozet');

    // Generate filename
    const startStr = startDate.toLocaleDateString('tr-TR').replace(/\//g, '-');
    const endStr = endDate.toLocaleDateString('tr-TR').replace(/\//g, '-');
    const defaultFilename = `Faturalar_${startStr}_${endStr}.xlsx`;

    // Download
    XLSX.writeFile(workbook, filename || defaultFilename);
};

export default exportInvoicesToExcel;
